/**
 * Fit marks on product cards, from the garage.
 *
 * Outside a machine's own view — a category, a brand page, a search, the
 * homepage's generic rows — a card says when the product fits the garage's
 * chosen machine: "✓ Fits your <machine>" in the confirmation green (the
 * other saved machines are no longer marked — Lucas, 2026-10-13). On a
 * collection or search results grid the fitting products are moved to the
 * front of the page. Inside a machine's view (data-machine-scope, or a
 * vehicle collection) every product fits, so nothing is marked or moved.
 *
 * Fitment is collection membership, so the machine's feed (collection-feed.js,
 * cached per tab, already loaded for search) is the source: a product fits
 * when its handle is in the machine's collection. Cards that arrive later —
 * fetched rows, a re-rendered grid — are picked up by a mutation observer.
 */
(function () {
  'use strict';

  const sets = new Map(); // key → Set of handles
  let painting = false;
  let queued = false;
  let timer = null;

  async function handlesOf(key) {
    if (sets.has(key)) return sets.get(key);
    const products = await window.CollectionFeed.load(key);
    const set = new Set(products.map((p) => p.handle));
    sets.set(key, set);
    return set;
  }

  function cards() {
    return [...document.querySelectorAll('.product-card[data-product-handle]')]
      .filter((card) => !card.closest('[data-machine-scope], [data-vehicle-key]'));
  }

  function mark(text, variant) {
    const badge = document.createElement('span');
    badge.className = `badge ${variant} product-card__fit`;
    badge.textContent = text;
    return badge;
  }

  /** The grids whose order follows the machine: collection and search results, not the homepage rows. */
  function resultGrids() {
    return [...document.querySelectorAll('[data-collection-results] .product-grid, [data-search-global] .product-grid')]
      .filter((grid) => !grid.closest('[data-machine-scope], [data-vehicle-key]'));
  }

  /**
   * The products that fit the machine first, in their existing order among
   * themselves, the rest after in theirs (Lucas, 2026-10-13). On a category's
   * first page collection.js first adds the fitting parts the page lacks
   * (#119), so every fit leads there; on a search results page a fit on a
   * later page stays there. Nodes move only when the order would change, so
   * the observer settles.
   */
  function hoist(grid, fits) {
    const items = [...grid.children].filter((li) => li.querySelector('.product-card[data-product-handle]'));
    const fitting = items.filter((li) => fits.has(li.querySelector('.product-card').dataset.productHandle));
    if (!fitting.length || fitting.length === items.length) return;
    const wanted = [...fitting, ...items.filter((li) => !fitting.includes(li))];
    if (wanted.every((li, i) => li === items[i])) return;
    for (const li of wanted) grid.appendChild(li);
  }

  async function paint() {
    if (!window.CollectionFeed || !window.Garage) return;
    // The chosen machine alone (Lucas, 2026-10-13): a card says it fits the
    // machine the customer is shopping for, not every machine saved.
    const active = window.Garage.active();
    const fits = active ? await handlesOf(active.key).catch(() => null) : null;

    for (const card of cards()) {
      const slot = card.querySelector('[data-card-fits]');
      if (!slot) continue;
      const handle = card.dataset.productHandle;
      const mine = fits && fits.has(handle) ? active : null;
      const signature = mine ? mine.key : '';
      if (slot.dataset.fitsSignature === signature) continue;
      slot.dataset.fitsSignature = signature;
      slot.replaceChildren();
      if (!mine) { slot.hidden = true; continue; }
      slot.append(mark(`✓ Fits your ${mine.nickname || mine.label}`, 'badge--success'));
      slot.hidden = false;
    }

    if (fits) for (const grid of resultGrids()) hoist(grid, fits);
  }

  function schedule() {
    if (painting) { queued = true; return; }
    painting = true;
    paint().catch(() => {}).finally(() => {
      painting = false;
      if (queued) { queued = false; schedule(); }
    });
  }

  function later() {
    clearTimeout(timer);
    timer = setTimeout(schedule, 80);
  }

  function boot() {
    schedule();
    document.addEventListener('garage:changed', () => { sets.clear(); schedule(); });
    const observer = new MutationObserver((mutations) => {
      // Our own marks land in the slots; everything else may be new cards.
      if (mutations.some((m) => !(m.target instanceof Element && m.target.matches('[data-card-fits]')))) later();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.FitsBadges = { paint: schedule };
  if (document.readyState === 'complete') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
